/**
 * ExecutiveDiagnosticsCard — C-Suite dashboard tile. Aggregates the
 * distress-signal counts across the whole portfolio and points at the
 * top offending projects per signal so an exec can drill in fast.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  HeartPulse, TrendingDown, Flame, AlertTriangle, GitBranch, UserX,
  ShieldAlert, MegaphoneIcon, Rocket, Sparkles, Wrench, LifeBuoy, Loader2,
} from "lucide-react";
import { api } from "../../lib/api";

const ICONS = {
  SCHEDULE_SLIPPAGE:   TrendingDown,
  BUDGET_OVERRUN:      Flame,
  SCOPE_AT_RISK:       AlertTriangle,
  CRITICAL_PATH_DELAY: GitBranch,
  RESOURCE_CONSTRAINT: UserX,
  HIGH_RISK_EXPOSURE:  ShieldAlert,
  ISSUE_ESCALATION:    MegaphoneIcon,
  GO_LIVE_AT_RISK:     Rocket,
  BENEFITS_AT_RISK:    Sparkles,
  RECOVERY_MODE:       LifeBuoy,
  REMEDIATION_PLAN:    Wrench,
};

const SEVERITY_STYLES = {
  Red:   { chip: "bg-red-100 text-red-700",       dot: "bg-red-500" },
  Amber: { chip: "bg-amber-100 text-amber-700",   dot: "bg-amber-500" },
  Green: { chip: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
};

export default function ExecutiveDiagnosticsCard() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    api.portfolioDiagnostics()
      .then(d => { if (alive) setData(d); })
      .catch(e => { if (alive) setErr(e?.response?.data?.detail || e.message || "Failed to load"); });
    return () => { alive = false; };
  }, []);

  if (err) return (
    <div className="card p-5 border-red-200 bg-red-50 text-sm text-red-700" data-testid="exec-diagnostics-error">
      Failed to load diagnostics: {err}
    </div>
  );
  if (!data) return (
    <div className="card p-6 text-sm text-zinc-500 flex items-center gap-2" data-testid="exec-diagnostics-loading">
      <Loader2 size={14} className="animate-spin" /> Aggregating portfolio health…
    </div>
  );

  const { totals, worst_projects, signals } = data;

  return (
    <div className="card p-6" data-testid="exec-diagnostics-card">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-violet-100 text-violet-600 grid place-items-center">
            <HeartPulse size={20} />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">Portfolio Diagnostics</div>
            <div className="font-display text-lg font-bold text-zinc-900">Distress Signals</div>
          </div>
        </div>
        <div className="flex items-center gap-4 text-[13px]">
          <div data-testid="exec-diagnostics-red">
            <span className="w-2 h-2 rounded-full bg-red-500 inline-block mr-1.5 align-middle" />
            <b className="text-red-700">{totals.red}</b> <span className="text-zinc-500">RAG Red</span>
          </div>
          <div data-testid="exec-diagnostics-amber">
            <span className="w-2 h-2 rounded-full bg-amber-500 inline-block mr-1.5 align-middle" />
            <b className="text-amber-700">{totals.amber}</b> <span className="text-zinc-500">Amber</span>
          </div>
          <div data-testid="exec-diagnostics-green">
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block mr-1.5 align-middle" />
            <b className="text-emerald-700">{totals.green}</b> <span className="text-zinc-500">Green</span>
          </div>
        </div>
      </div>

      {/* Empty state */}
      {signals.length === 0 && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800" data-testid="exec-diagnostics-clean">
          No distress signals firing across the portfolio. 🎉
        </div>
      )}

      {/* Signal grid */}
      {signals.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4" data-testid="exec-signal-grid">
          {signals.map(s => {
            const st = SEVERITY_STYLES[s.severity] || SEVERITY_STYLES.Amber;
            const Icon = ICONS[s.code] || AlertTriangle;
            return (
              <div key={s.code} className="rounded-xl border border-zinc-200 p-3.5 hover:shadow-sm transition-shadow" data-testid={`exec-signal-${s.code}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-8 h-8 rounded-lg ${st.chip} grid place-items-center`}>
                    <Icon size={15} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold text-zinc-900 truncate">{s.label}</div>
                    <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">
                      {s.count} project{s.count !== 1 ? "s" : ""}
                    </div>
                  </div>
                </div>
                <ul className="space-y-1.5">
                  {s.top_offenders.map((o, i) => (
                    <li
                      key={i}
                      className="text-[12px] text-zinc-700 cursor-pointer hover:text-violet-700 flex items-start gap-1.5"
                      onClick={() => nav(`/projects/${o.project_id}?tab=diagnostics`)}
                      data-testid={`exec-offender-${s.code}-${i}`}
                      title={`${o.project_name} — ${o.trigger}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${st.dot} shrink-0 mt-1.5`} />
                      <span className="min-w-0 flex-1">
                        <span className="font-semibold text-zinc-900">{o.project_name}</span>
                        <span className="text-zinc-500"> — {o.trigger}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {/* Worst-offending projects */}
      {worst_projects?.length > 0 && (
        <div className="rounded-xl bg-zinc-50 border border-zinc-200 p-3" data-testid="exec-worst-projects">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
            Projects requiring executive attention
          </div>
          <div className="flex items-center flex-wrap gap-2">
            {worst_projects.map(p => (
              <button
                key={p.project_id}
                onClick={() => nav(`/projects/${p.project_id}?tab=diagnostics`)}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-red-200 hover:border-red-400 text-[12px] font-medium text-red-700 transition-colors"
                data-testid={`exec-worst-${p.project_id}`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                {p.project_name}
                <span className="font-mono text-red-500">· {p.reds}R/{p.ambers}A</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
